import { z } from "zod";

// CLAUDE.md §1 — every tunable is env-driven and validated at boot.
const EnvSchema = z.object({
  VITE_PUBLIC_URL: z.string().url(),
  /** ISO 3166-1 alpha-2 country code — drives currency, locale, statutory
   *  rule pack, climate zone, cultural pack, vendors. See lib/i18n/country.ts. */
  VITE_DEFAULT_COUNTRY: z.string().length(2),
  VITE_BRAND_NAME: z.string().min(1),
  VITE_BRAND_PARTNER: z.string().min(1),
  VITE_BRAND_SITE_URL: z.string().url(),

  VITE_SUB_W_STYLE: z.coerce.number(),
  VITE_SUB_W_PRICE: z.coerce.number(),
  VITE_SUB_W_LEAD: z.coerce.number(),
  VITE_SUB_W_VENDOR: z.coerce.number(),
  VITE_SUB_W_MEMORY: z.coerce.number(),

  VITE_WHY_MAX_DEPTH: z.coerce.number().int().positive(),
  VITE_WHY_TOP_K_PATHS: z.coerce.number().int().positive(),

  VITE_MEMORY_MIN_ACCEPTS: z.coerce.number().int().positive(),
  VITE_MEMORY_MAX_REJECT_RATIO: z.coerce.number(),

  // Reasoning thresholds — env-driven, never magic numbers in code.
  VITE_STYLE_COHESION_THRESHOLD: z.coerce.number().min(0).max(1),
  VITE_BUDGET_LINE_TARGET: z.coerce.number().min(0).max(1),
  VITE_COHESION_TOLERANCE_PP: z.coerce.number().min(0).max(100),
  VITE_APPROVAL_AGE_DAYS: z.coerce.number().int().nonnegative(),
  /** SKU price (INR) at or above which a typed Approval is required.
   *  Drives detectMissingApproval + Tracker Approvals tab. */
  VITE_HIGH_VALUE_THRESHOLD_INR: z.coerce.number().int().nonnegative(),
  VITE_LEAD_TIME_BUFFER_DAYS: z.coerce.number().int().nonnegative(),
  VITE_SCHEDULE_TRAVEL_BUFFER_MIN: z.coerce.number().int().nonnegative(),
  VITE_TASK_DOING_WINDOW_DAYS: z.coerce.number().int().positive(),
  VITE_ESTIMATE_TAX_RATE: z.coerce.number().min(0).max(1),
  VITE_ESTIMATE_LABOUR_PER_SQFT_INR: z.coerce.number().int().positive(),
  // Tier bands (INR thresholds) and indicative tier values for prospects.
  // Every "₹ … L" chip on a lead card flows from these.
  VITE_TIER_BAND_PREMIUM_INR: z.coerce.number().int().positive(),
  VITE_TIER_BAND_LUXURY_INR:  z.coerce.number().int().positive(),
  VITE_TIER_VALUE_STANDARD_INR: z.coerce.number().int().positive(),
  VITE_TIER_VALUE_PREMIUM_INR:  z.coerce.number().int().positive(),
  VITE_TIER_VALUE_LUXURY_INR:   z.coerce.number().int().positive(),
  // Lead pipeline escalation thresholds — when a lead's age exceeds
  // these, the next-best-action chip escalates (e.g. "first-touch reply"
  // → "send qualifying questions").
  VITE_LEAD_NEW_ESCALATE_DAYS:        z.coerce.number().int().nonnegative(),
  VITE_LEAD_QUALIFIED_ESCALATE_DAYS:  z.coerce.number().int().nonnegative(),
  VITE_LEAD_PROPOSAL_ESCALATE_DAYS:   z.coerce.number().int().nonnegative(),

  VITE_DEMO_SEED: z.string().min(1),
  VITE_CONTACT_EMAIL: z.string().email(),
  VITE_LS_RULE_CANVAS: z.string().min(1),
  VITE_LS_APPLIED_RULES: z.string().min(1),
  VITE_LS_INBOX_DECISIONS: z.string().min(1),
  VITE_LS_MEMORY: z.string().min(1),
  VITE_LS_OPTIMIZER_PENDING: z.string().min(1),
  VITE_LS_SESSION_ID_PREFIX: z.string().min(1),
  VITE_LS_CONNECTIONS: z.string().min(1),
  VITE_LS_FLOW_ENABLED: z.string().min(1),
  VITE_LS_ACTIVE_PROJECT: z.string().min(1),
  VITE_LS_ACTIVE_PROJECT_LEGACY: z.string().min(1),
  VITE_LS_RENDER_ROOM: z.string().min(1),
  VITE_LS_PROJECT_RULES_PREFIX: z.string().min(1),

  // Cockpit layout — Apple-style breathing room, env-driven so the
  // studio can tune the right-rail width without a code change.
  VITE_COCKPIT_RAIL_W_PX: z.coerce.number().int().positive(),
  VITE_COCKPIT_GAP_PX:    z.coerce.number().int().positive(),

  /** Confidence floor for the onboarding intake detector — under
   *  this band, the detector asks a clarifying question instead of
   *  silently mapping. CLAUDE.md §1. */
  VITE_INTAKE_CONFIDENCE_FLOOR: z.coerce.number().min(0).max(1),

  /** localStorage keys for onboarding outputs — CLAUDE.md §1, all
   *  string keys are env-driven so the schema can evolve. */
  VITE_LS_USER_PROJECTS: z.string().min(1),
  VITE_LS_INGEST_LATEST: z.string().min(1),

  /** Name of the global the Foyr Neo editor exposes (verified
   *  2026-05-17: `neoEditor`). Env-driven so a Foyr rename does
   *  not require a code change. */
  VITE_FOYR_EDITOR_GLOBAL: z.string().min(1),

  /** A.1 SOTA-canvas feature flag. When "true", the Design page uses
   *  the tldraw-backed FloorPlan2DTldraw component as a read-only
   *  preview alongside the production SVG renderer. Default off so
   *  the existing path stays stable. */
  VITE_FLOOR_PLAN_TLDRAW: z.enum(["true", "false"]),

  /** Foyr Neo project id (24-char hex) used by the cockpit's
   *  one-click demo. Real ObjectId we verified live; env-driven
   *  so a different account can swap in its own without code
   *  changes. CLAUDE.md §1. */
  VITE_FOYR_DEMO_PROJECT_ID: z.string().length(24),

  /** localStorage key for ingested typed graphs keyed by project id. */
  VITE_LS_PROJECT_GRAPHS: z.string().min(1),

  // Admin credentials — sourced from env (vite injects at build time).
  // CLAUDE.md §1 — never literal in source. v1.1 swaps to real SSO.
  VITE_ADMIN_EMAIL: z.string().email(),
  VITE_ADMIN_PASSWORD: z.string().min(4),
});

const parsed = EnvSchema.safeParse({
  VITE_PUBLIC_URL: import.meta.env.VITE_PUBLIC_URL ?? "https://foyr.hypergraphmind.com",
  VITE_DEFAULT_COUNTRY: import.meta.env.VITE_DEFAULT_COUNTRY ?? "IN",
  VITE_BRAND_NAME: import.meta.env.VITE_BRAND_NAME ?? "HyperGraphMind",
  VITE_BRAND_PARTNER: import.meta.env.VITE_BRAND_PARTNER ?? "Foyr",
  VITE_BRAND_SITE_URL: import.meta.env.VITE_BRAND_SITE_URL ?? "https://hypergraphmind.com",
  VITE_SUB_W_STYLE: import.meta.env.VITE_SUB_W_STYLE ?? "0.45",
  VITE_SUB_W_PRICE: import.meta.env.VITE_SUB_W_PRICE ?? "0.25",
  VITE_SUB_W_LEAD: import.meta.env.VITE_SUB_W_LEAD ?? "0.20",
  VITE_SUB_W_VENDOR: import.meta.env.VITE_SUB_W_VENDOR ?? "0.05",
  VITE_SUB_W_MEMORY: import.meta.env.VITE_SUB_W_MEMORY ?? "0.05",
  VITE_WHY_MAX_DEPTH: import.meta.env.VITE_WHY_MAX_DEPTH ?? "5",
  VITE_WHY_TOP_K_PATHS: import.meta.env.VITE_WHY_TOP_K_PATHS ?? "3",
  VITE_MEMORY_MIN_ACCEPTS: import.meta.env.VITE_MEMORY_MIN_ACCEPTS ?? "5",
  VITE_MEMORY_MAX_REJECT_RATIO: import.meta.env.VITE_MEMORY_MAX_REJECT_RATIO ?? "0.15",
  VITE_STYLE_COHESION_THRESHOLD: import.meta.env.VITE_STYLE_COHESION_THRESHOLD ?? "0.82",
  VITE_BUDGET_LINE_TARGET: import.meta.env.VITE_BUDGET_LINE_TARGET ?? "0.25",
  VITE_COHESION_TOLERANCE_PP: import.meta.env.VITE_COHESION_TOLERANCE_PP ?? "5",
  VITE_APPROVAL_AGE_DAYS: import.meta.env.VITE_APPROVAL_AGE_DAYS ?? "5",
  VITE_HIGH_VALUE_THRESHOLD_INR: import.meta.env.VITE_HIGH_VALUE_THRESHOLD_INR ?? "100000",
  VITE_LEAD_TIME_BUFFER_DAYS: import.meta.env.VITE_LEAD_TIME_BUFFER_DAYS ?? "14",
  VITE_SCHEDULE_TRAVEL_BUFFER_MIN:   import.meta.env.VITE_SCHEDULE_TRAVEL_BUFFER_MIN   ?? "30",
  VITE_TASK_DOING_WINDOW_DAYS:       import.meta.env.VITE_TASK_DOING_WINDOW_DAYS       ?? "7",
  VITE_ESTIMATE_TAX_RATE:            import.meta.env.VITE_ESTIMATE_TAX_RATE            ?? "0.18",
  VITE_ESTIMATE_LABOUR_PER_SQFT_INR: import.meta.env.VITE_ESTIMATE_LABOUR_PER_SQFT_INR ?? "450",
  VITE_TIER_BAND_PREMIUM_INR:        import.meta.env.VITE_TIER_BAND_PREMIUM_INR        ?? "1000000",
  VITE_TIER_BAND_LUXURY_INR:         import.meta.env.VITE_TIER_BAND_LUXURY_INR         ?? "3000000",
  VITE_TIER_VALUE_STANDARD_INR:      import.meta.env.VITE_TIER_VALUE_STANDARD_INR      ?? "650000",
  VITE_TIER_VALUE_PREMIUM_INR:       import.meta.env.VITE_TIER_VALUE_PREMIUM_INR       ?? "1800000",
  VITE_TIER_VALUE_LUXURY_INR:        import.meta.env.VITE_TIER_VALUE_LUXURY_INR        ?? "5000000",
  VITE_LEAD_NEW_ESCALATE_DAYS:       import.meta.env.VITE_LEAD_NEW_ESCALATE_DAYS       ?? "2",
  VITE_LEAD_QUALIFIED_ESCALATE_DAYS: import.meta.env.VITE_LEAD_QUALIFIED_ESCALATE_DAYS ?? "5",
  VITE_LEAD_PROPOSAL_ESCALATE_DAYS:  import.meta.env.VITE_LEAD_PROPOSAL_ESCALATE_DAYS  ?? "7",
  VITE_DEMO_SEED: import.meta.env.VITE_DEMO_SEED ?? "foyr-hgm-v1-2026-05-15",
  VITE_CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL ?? "contact@hypergraphmind.com",
  VITE_LS_RULE_CANVAS: import.meta.env.VITE_LS_RULE_CANVAS ?? "hgm.rule.canvas.v1",
  VITE_LS_APPLIED_RULES: import.meta.env.VITE_LS_APPLIED_RULES ?? "hgm.applied.rules.v1",
  VITE_LS_INBOX_DECISIONS: import.meta.env.VITE_LS_INBOX_DECISIONS ?? "hgm.inbox.decisions.v1",
  VITE_LS_MEMORY: import.meta.env.VITE_LS_MEMORY ?? "foyrmind.memory.v1",
  VITE_LS_OPTIMIZER_PENDING: import.meta.env.VITE_LS_OPTIMIZER_PENDING ?? "foyrmind.optimizer.pending.v1",
  VITE_LS_SESSION_ID_PREFIX: import.meta.env.VITE_LS_SESSION_ID_PREFIX ?? "foyrmind.session-id.",
  VITE_LS_CONNECTIONS: import.meta.env.VITE_LS_CONNECTIONS ?? "foyrmind.connections.v1",
  VITE_LS_FLOW_ENABLED: import.meta.env.VITE_LS_FLOW_ENABLED ?? "foyrmind.flows.enabled.v1",
  VITE_LS_ACTIVE_PROJECT:        import.meta.env.VITE_LS_ACTIVE_PROJECT        ?? "foyrmind.activeProjectId",
  VITE_LS_ACTIVE_PROJECT_LEGACY: import.meta.env.VITE_LS_ACTIVE_PROJECT_LEGACY ?? "hgm.openProject.v1",
  VITE_LS_RENDER_ROOM:           import.meta.env.VITE_LS_RENDER_ROOM           ?? "foyrmind.render.roomId.v1",
  // Per-project promoted-rule storage (Spec 07). `${prefix}${projectId}.rules.v1`
  // holds string[] of promoted rule ids; `${prefix}${projectId}.custom-rules.v1`
  // holds the user-authored DesignerRule[]. The old global key is migrated
  // on first read into the active project's bucket. See lib/projectRules.ts.
  VITE_LS_PROJECT_RULES_PREFIX:  import.meta.env.VITE_LS_PROJECT_RULES_PREFIX  ?? "foyrmind.project.",
  VITE_COCKPIT_RAIL_W_PX:        import.meta.env.VITE_COCKPIT_RAIL_W_PX        ?? "420",
  VITE_COCKPIT_GAP_PX:           import.meta.env.VITE_COCKPIT_GAP_PX           ?? "28",
  VITE_INTAKE_CONFIDENCE_FLOOR:  import.meta.env.VITE_INTAKE_CONFIDENCE_FLOOR  ?? "0.55",
  VITE_LS_USER_PROJECTS:         import.meta.env.VITE_LS_USER_PROJECTS         ?? "foyrmind.userProjects.v1",
  VITE_LS_INGEST_LATEST:         import.meta.env.VITE_LS_INGEST_LATEST         ?? "foyrmind.ingest.latest",
  VITE_FOYR_EDITOR_GLOBAL:       import.meta.env.VITE_FOYR_EDITOR_GLOBAL       ?? "neoEditor",
  VITE_FOYR_DEMO_PROJECT_ID:     import.meta.env.VITE_FOYR_DEMO_PROJECT_ID     ?? "6a09a472a71f10b24487230c",
  VITE_FLOOR_PLAN_TLDRAW:        import.meta.env.VITE_FLOOR_PLAN_TLDRAW        ?? "false",
  VITE_LS_PROJECT_GRAPHS:        import.meta.env.VITE_LS_PROJECT_GRAPHS        ?? "foyrmind.projectGraphs.v1",
  VITE_ADMIN_EMAIL: import.meta.env.VITE_ADMIN_EMAIL ?? "gaurav@hypergraphmind.com",
  VITE_ADMIN_PASSWORD: import.meta.env.VITE_ADMIN_PASSWORD ?? "hypermind",
});

if (!parsed.success) {
  // CLAUDE.md §5 — never silently eat. Surface to the console so the
  // first thing a developer sees is the schema problem.
  // eslint-disable-next-line no-console
  console.error("[env] schema validation failed", parsed.error.format());
  throw new Error("Environment validation failed; see console for schema errors.");
}

export const env = parsed.data;
export type Env = z.infer<typeof EnvSchema>;
